import { useEffect, useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { ErrorMessage as FoundationsErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import Raven from 'raven-js';
import { SENTRY_KEYS } from 'framework-asset-preview-lib/internal/shared/constants/sentry';
import { Metrics } from 'framework-asset-preview-lib/internal/metrics';
import { useAssetPreview } from 'framework-asset-preview-lib/internal/shared/providers/AssetPreviewProvider';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function ErrorMessageInner({
  applicationId,
  errorType,
  objectId,
  objectType
}) {
  const uiErrorMessageType = useMemo(() => {
    switch (errorType) {
      default:
        return 'badRequest';
      case SENTRY_KEYS.CRM_OBJECT_NOT_FOUND:
      case SENTRY_KEYS.PREVIEW_COMPONENT_NOT_FOUND:
        return 'notFound';
    }
  }, [errorType]);
  const message = useMemo(() => {
    switch (errorType) {
      default:
        return 'frameworkAssetPreviewLib.error.generalError';
      case SENTRY_KEYS.CRM_OBJECT_NOT_FOUND:
        return 'frameworkAssetPreviewLib.error.crmObjectNotFound';
      case SENTRY_KEYS.PREVIEW_COMPONENT_NOT_FOUND:
        return 'frameworkAssetPreviewLib.error.previewNotFound';
      case SENTRY_KEYS.TOP_LEVEL_ASSETS_TAB:
        return 'frameworkAssetPreviewLib.error.associationsError';
      case SENTRY_KEYS.PERFORMANCE_TAB:
        return 'frameworkAssetPreviewLib.error.performanceTabError';
    }
  }, [errorType]);
  const isAPIError = useMemo(() => errorType === SENTRY_KEYS.CRM_SEARCH_ERROR || errorType === SENTRY_KEYS.CRM_PROPERTIES_ERROR, [errorType]);
  useEffect(() => {
    /**
     * The actual error is already logged by our ErrorBoundary component
     */
    if (errorType === SENTRY_KEYS.ERROR_BOUNDARY) {
      return;
    }
    const dimensions = {
      application_id: applicationId,
      object_type_id: objectType || 'no-object-type'
    };
    switch (errorType) {
      default:
        Raven.captureException(new Error(`${errorType}`), {
          extra: {
            objectId,
            objectType
          },
          fingerprint: [errorType],
          tags: {
            lib: 'framework-asset-preview-lib'
          }
        });
        break;
      case SENTRY_KEYS.CRM_OBJECT_NOT_FOUND:
        Metrics.counter('crm-object-not-found', dimensions).increment();
        break;
      case SENTRY_KEYS.CRM_PROPERTIES_ERROR:
        Metrics.counter('crm-properties-error', dimensions).increment();
        break;
      case SENTRY_KEYS.CRM_SEARCH_ERROR:
        Metrics.counter('crm-search-error', dimensions).increment();
        break;
      case SENTRY_KEYS.CRM_TYPE_DEFINITION_ERROR:
        Metrics.counter('type-definition-error', dimensions).increment();
        break;
      case SENTRY_KEYS.NON_CRM_OBJECT_ERROR:
        Metrics.counter('non-crm-object-error', dimensions).increment();
        break;
      case SENTRY_KEYS.PERFORMANCE_TAB:
        Metrics.counter('performance-tab-error', dimensions).increment();
        break;
      case SENTRY_KEYS.PREVIEW_COMPONENT_NOT_FOUND:
        Metrics.counter('preview-component-not-found', dimensions).increment();
        break;
    }
  }, [applicationId, errorType, objectId, objectType]);
  return /*#__PURE__*/_jsxs(FoundationsErrorMessage, {
    type: uiErrorMessageType,
    "data-test-id": "ui-error-message",
    children: [/*#__PURE__*/_jsx("p", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: message
      })
    }), !isAPIError && errorType !== SENTRY_KEYS.ERROR_BOUNDARY && /*#__PURE__*/_jsx("p", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "frameworkAssetPreviewLib.error.generalError"
      })
    })]
  });
}
function ConnectedErrorMessage(props) {
  const {
    applicationId
  } = useAssetPreview();
  return /*#__PURE__*/_jsx(ErrorMessageInner, Object.assign({
    applicationId: applicationId
  }, props));
}
export default function ErrorMessage(props) {
  return props.applicationId ? /*#__PURE__*/_jsx(ErrorMessageInner, Object.assign({}, props, {
    applicationId: props.applicationId
  })) : /*#__PURE__*/_jsx(ConnectedErrorMessage, Object.assign({}, props));
}